import { Skeleton, Stack, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import AttachmentsRenderer from '../../../../attachments/AttachmentsRenderer';
import EditablePanel, { EditablePanelProps } from '../../../../components/EditablePanel';
import { ATTACHMENT_RENDERER_SKELETON_HEIGHT } from '../../../../constants/constants';
import I18n from '../../../../i18n/I18n';
import { APPROVAL_EDITABLE_PANEL_CONTENT_SX } from '../../../constants';
import { useReviewApprovalContext } from '../../hooks/useReviewApprovalContext';
import CreateApprovalFormEditablePanelHeader from '../CreateApprovalFormEditablePanelHeader';

const headerActionsSx = { flex: 1 };

export type ApprovalAttachmentsViewProps = Pick<EditablePanelProps, 'isCollapsable' | 'defaultCollapsed'>;

export default function ApprovalAttachmentsView({ isCollapsable, defaultCollapsed }: ApprovalAttachmentsViewProps) {
    const {
        isFetchingApprovalData,
        isFetchingApprovalAttachmentsData,
        errorFetchingApprovalAttachmentsData,
        approvalAttachmentsData
    } = useReviewApprovalContext();

    let attachmentContent: ReactNode = (
        <Typography
            color="textSecondary"
            sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <I18n token="approval.attachments.noAttachments" />
        </Typography>
    );

    if (isFetchingApprovalData || isFetchingApprovalAttachmentsData) {
        attachmentContent = Array.from({ length: 2 }).map((_, skeletonIndex) => (
            <Skeleton key={skeletonIndex} variant="rounded" width="100%" height={ATTACHMENT_RENDERER_SKELETON_HEIGHT} />
        ));
    } else if (errorFetchingApprovalAttachmentsData) {
        attachmentContent = (
            <Typography color="error" sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <I18n token="approval.attachments.fetch.error" />
            </Typography>
        );
    } else if (approvalAttachmentsData.length > 0) {
        attachmentContent = approvalAttachmentsData.map((attachment) => (
            <AttachmentsRenderer key={attachment.id} file={attachment} isPreview={true} />
        ));
    }

    return (
        <EditablePanel
            Header={
                <CreateApprovalFormEditablePanelHeader
                    titleToken="approval.attachments.header"
                    titleSuffix={
                        approvalAttachmentsData.length > 0 ? approvalAttachmentsData.length.toString() : undefined
                    }
                />
            }
            isCollapsable={isCollapsable}
            defaultCollapsed={defaultCollapsed}
            headerActionsSx={headerActionsSx}
        >
            <Stack alignItems="center" sx={[APPROVAL_EDITABLE_PANEL_CONTENT_SX, { gap: 1 }]}>
                {attachmentContent}
            </Stack>
        </EditablePanel>
    );
}
